import type { Connection, GithubOptionsPatch } from '@hugo/core'
import { useQueryClient } from '@tanstack/react-query'
import { TriangleAlertIcon } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { queryKeys, useRunTool } from '@/lib/items'

/**
 * GitHub's options under its row in Settings → Apps (decision 108): the Projects board column that
 * means Testing, and whether Testing already makes an assigned task done in hugo. Says when the
 * connection lacks access to boards, with a way to grant it.
 */
export function GithubOptions({
  connection,
  onReconnect,
}: {
  connection: Connection
  onReconnect: () => void
}) {
  const options = connection.github
  const { run } = useRunTool()
  const queryClient = useQueryClient()
  const columnId = useId()
  const doneId = useId()
  const [column, setColumn] = useState(options?.testingStatus ?? 'Testing')
  useEffect(() => setColumn(options?.testingStatus ?? 'Testing'), [options?.testingStatus])
  if (!options) return null

  const save = async (patch: GithubOptionsPatch, done: string) => {
    const result = await run(
      'connection.update_options',
      { connector: 'github', github: patch },
      { done, failed: 'Could not save the GitHub options' },
    )
    if (result) await queryClient.invalidateQueries({ queryKey: queryKeys.connectors })
  }
  const saveColumn = () => {
    const name = column.trim()
    if (!name || name === options.testingStatus) return setColumn(options.testingStatus)
    void save({ testingStatus: name }, `Testing column: ${name}`)
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-3 text-sm" data-testid="github-options">
      {connection.missingScopes.includes('project') ? (
        <div
          className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
          data-testid="github-missing-scope"
        >
          <p className="flex items-start gap-2 text-muted-foreground">
            <TriangleAlertIcon
              aria-hidden="true"
              className="mt-0.5 size-4 shrink-0 text-amber-600"
            />
            hugo cannot see your GitHub Projects boards yet. Connect again to allow it.
          </p>
          <Button size="sm" variant="outline" onClick={onReconnect}>
            Connect again
          </Button>
        </div>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <label htmlFor={columnId} className="font-medium">
          Testing column
        </label>
        <Input
          id={columnId}
          className="h-9 sm:max-w-56"
          value={column}
          maxLength={60}
          onChange={(e) => setColumn(e.target.value)}
          onBlur={saveColumn}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveColumn()
          }}
        />
        <p className="text-xs text-muted-foreground">
          The column on your GitHub Projects board that means testing.
        </p>
      </div>
      <fieldset className="flex flex-col gap-1.5" aria-describedby={doneId}>
        <legend className="mb-1.5 font-medium">Done in hugo when the issue is</legend>
        {(
          [
            ['testing_or_closed', `In ${options.testingStatus} or closed`],
            ['closed', 'Closed'],
          ] as const
        ).map(([value, label]) => (
          <label key={value} className="flex items-center gap-2">
            <input
              type="radio"
              name="github-done-when"
              className="size-4 accent-primary"
              checked={options.doneWhen === value}
              onChange={() =>
                void save({ doneWhen: value }, `Done in hugo when: ${label.toLowerCase()}`)
              }
            />
            {label}
          </label>
        ))}
        <p id={doneId} className="text-xs text-muted-foreground">
          Marking a GitHub task done in hugo asks what GitHub should do.
        </p>
      </fieldset>
    </div>
  )
}
